import React, { useState, useEffect } from 'react'
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Modal,
  Table,
  Upload,
  Input as AntdInput,
  Divider,
  Popover,
  Popconfirm,
  Select,
  DatePicker
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import {
  notificationsAtom,
  editContactAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import PhoneInput from '../../../shared-components/form/PhoneInput';
import { contactTypes } from '../../../../utils';
import { getMobileDatePickerFooter } from '../../../../functions';

function EditContactModal() {
  const { t, i18n } = useTranslation();
  const { Dragger } = Upload;
  const { Option } = Select;
  const [open, setOpen] = useState(false);
  const [editContact, setEditContact] = useRecoilState(editContactAtom)
  const [contactData, setContactData] = useState(null);
  const [submittingContact, setSubmittingContact] = useState(false);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);

  const draggerProps = {
    name: 'file',
    multiple: false,
    showUploadList: true,
    maxCount: 1,
    fileList: contactData?.file ? [contactData?.file] : [],
    beforeUpload: (file) => {
      setContactData({
        ...contactData,
        file
      });
      return false;
    },
    ondrop: (e) => {
    },
    onRemove: (e) => {
      setContactData({
        ...contactData,
        file: null
      });
    }
  };

  function checkIfContactValid() {
    const obj = {
      valid: false,
      validName: false,
      validLastName: false,
      validEmail: false,
      validPhone: false
    };
    if (contactData?.firstName?.length > 0) {
      obj.validName = true;
    }
    if (contactData?.lastName?.length > 0) {
      obj.validLastName = true;
    }
    if (contactData?.email?.length > 0) {
      obj.validEmail = true;
    }
    if (contactData?.phone?.length > 0) {
      obj.validPhone = true;
    }
    obj.valid = obj.validName && obj.validEmail && obj.validLastName && obj.validPhone;
    return obj;
  }

  function upload() {
    const formdata = new FormData();
      formdata.append('file', contactData?.file);
      formdata.append('phone', contactData?.phone);
      formdata.append('email', contactData?.email);
      formdata.append('firstName', contactData?.firstName);
      formdata.append('lastName', contactData?.lastName);
      formdata.append('birthdate', contactData?.birthdate);
      formdata.append('additionalContactId', contactData?.id);
      formdata.append('contactType', contactData?.contactType);
      setSubmittingContact(true);
      api.put('leads/lead/contacts', formdata)
      .then((response) => {
        setSubmittingContact(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          setEditContact({
            ...editContact,
            reload: true
          });
        }
      })
      .catch((error) => {
        setSubmittingContact(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  useEffect(() => {
    if (editContact?.contact) {
      setOpen(true);
      setContactData({
          ...editContact?.contact,
          file: null,
      });
    } else {
      setOpen(false);
    }
  }, [editContact]);

  return (
    <Modal
      width="80%"
      title={`${contactData?.firstName} ${contactData?.lastName}`}
      open={open}
      onCancel={() => {
        setEditContact({
          ...editContact,
          contact: null
        });
      }}
      footer={[]}
    >
      <div className="flex flex-col">
        <div className="grid grid-cols-4 mt-4 gap-2">
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <span>{t('name')}</span>
            <AntdInput
              className="h-14"
              value={contactData?.firstName}
              disabled={submittingContact}
              onChange={(obj) => setContactData({
                ...contactData,
                firstName: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <span>{t('last_name')}</span>
            <AntdInput
              className="h-14"
              value={contactData?.lastName}
              disabled={submittingContact}
              onChange={(obj) => setContactData({
                ...contactData,
                lastName: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <span>{t('email')}</span>
            <AntdInput
              className="h-14"
              value={contactData?.email}
              disabled={submittingContact}
              onChange={(obj) => setContactData({
                ...contactData,
                email: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <span>{t('phone')}</span>
            <PhoneInput
              type="phone"
              name="phone"
              id="phone"
              className="h-12"
              disabled={submittingContact}
              autoComplete="phone"
              value={contactData?.phone}
              onChange={(number) => {
                setContactData({
                  ...contactData,
                  phone: number
                });
              }}
            />  
          </div>
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <p>{t('birthdate')}</p>
            <DatePicker
              className="h-12 w-full"
              format="YYYY-MM-DD"
              inputReadOnly
              onChange={(date) => {
                setContactData({
                  ...contactData,
                  birthdate: date
                })
              }}
              value={contactData?.birthdate ? dayjs(contactData?.birthdate) : null}
              showSecond={false}
              type="primary"
              size="small"
              renderExtraFooter={() => getMobileDatePickerFooter(t)}
              placement="bottomLeft"
            />
          </div>
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <span>{t('contact_type')}</span>
            <Select
              showSearch
              className="w-full h-12"
              filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
              value={contactData?.contactType}
              loading={submittingContact}
              disabled={submittingContact}
              onChange={(obj) => setContactData({
                ...contactData,
                contactType: obj
              })}
            >
              {_.map(contactTypes, (c) => (
                <Option value={c.id}>{t(c.name)}</Option>
              ))}
            </Select>
          </div>
        </div>
        <Dragger className="h-28 mt-5" disabled={submittingContact} {...draggerProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {t('click_or_drag_content')}
          </p>
        </Dragger>
        <Popover
          content={!checkIfContactValid().valid ? (
            <>
              {!checkIfContactValid().validName && (
                <p>{t('must_enter_contact_name')}</p>
              )}
              {!checkIfContactValid().validLastName && (
                <p>{t('must_enter_contact_last_name')}</p>
              )}
              {!checkIfContactValid().validEmail && (
                <p>{t('must_enter_contact_email')}</p>
              )}
              {!checkIfContactValid().validPhone && (
                <p>{t('must_enter_contact_phone')}</p>
              )}
            </>
          ) : null}
        >
          <Button
            disabled={!checkIfContactValid().valid}
            loading={submittingContact}
            type="primary"
            className="w-1/3 mt-10 mx-auto"
            onClick={() => upload()}
          >
            {t('update')}
          </Button>
        </Popover>
      </div>
    </Modal>
  );
}

export default EditContactModal;
